import { api, mutate } from './api'
import { businessSettings } from './business-settings'
import { getMe, worksAlone } from './me'
import { buzz } from './haptic'
import { clear, el, parseMoney, toast } from './ui'
import { openAdd, payLine, type PersonRow } from './screens/people'
import type { OwnerPaySetting, PartnerRow } from './owner-pay-rules'
import './close-out.css'

// Your business: "How do you get paid?" and "How your people are paid" (0097; Liam, 29 Oct 2026: "we need to be set up
// for multiple different scenarios", "you should be able to specify what workers get ie grant 60% but sendi is day rate
// 170", "i also should be able to add a worker"; design E1 on https://claude.ai/artifact/EqUcAcKbAFKUHj73jmp9gh).
// The setting fills in the close-out's last step, "Where the day's money goes"; every amount there can still be changed.
// The people's pay is the same setting as on People (one save, two places).

type Mode = OwnerPaySetting['mode']
const WAYS: { mode: Mode; title: string; line: string }[] = [
  { mode: 'keep', title: 'I keep what’s left', line: 'After workers are paid, the rest is yours.' },
  { mode: 'wage', title: 'I pay myself a wage', line: 'A day rate, or a set amount each month.' },
  { mode: 'split', title: 'Partners share it', line: 'Each partner a percent, with a minimum if you like.' },
  { mode: 'ask', title: 'Ask me each day', line: 'It changes too much: you type it on the day.' },
]

interface Draft { mode: Mode; base: 'after' | 'all'; wage_kind: 'day' | 'month'; wage: string; partners: { name: string; pct: string; min: string }[] }

export function ownerPayCard(): HTMLElement {
  const card = el('section', { class: 'business-form owner-pay', 'aria-label': 'How you and your people get paid' })
  card.append(el('p', { class: 'muted' }, 'Loading how you get paid…'))
  void fill(card)
  return card
}

async function fill(card: HTMLElement): Promise<void> {
  let setting: { pay: OwnerPaySetting; partners: PartnerRow[] }
  let people: PersonRow[] = []
  try {
    ;[setting, people] = await Promise.all([
      api<{ pay: OwnerPaySetting; partners: PartnerRow[] }>('/api/owner-pay'),
      api<{ users: PersonRow[] }>('/api/users', 'users').then((d) => d.users),
    ])
  } catch {
    clear(card)
    card.append(el('p', { class: 'form-error' }, 'How you get paid needs signal to load. Open Your business again.'))
    return
  }
  const me = getMe()?.workerName?.trim() || ''
  const draft: Draft = {
    mode: setting.pay.mode, base: setting.pay.base, wage_kind: setting.pay.wage_kind ?? 'day',
    wage: setting.pay.wage_pence !== null ? (setting.pay.wage_pence / 100).toFixed(2) : '',
    partners: setting.partners.length ? setting.partners.map((p) => ({ name: p.name, pct: String(p.pct), min: p.min_pence ? (p.min_pence / 100).toFixed(2) : '' }))
      : [{ name: me, pct: '60', min: '' }],
  }
  const reload = (): void => { void fill(card) }
  draw(card, draft, setting.pay.version, people, reload)
}

function draw(card: HTMLElement, draft: Draft, version: number, people: PersonRow[], reload: () => void): void {
  clear(card)
  const keeps = `${businessSettings().short_name || 'The business'}`
  card.append(el('h3', {}, 'How do you get paid?'), el('p', { class: 'muted' }, 'Close out the day fills in each amount from this, and you can change any day by hand.'))
  const tiles = el('div', { class: 'owner-pay-tiles', role: 'group', 'aria-label': 'How you get paid' })
  for (const w of WAYS) {
    const on = draft.mode === w.mode
    const b = el('button', { type: 'button', class: `owner-pay-tile${on ? ' is-on' : ''}`, 'aria-pressed': String(on) }, el('strong', {}, w.title), el('span', {}, w.line))
    b.addEventListener('click', () => { draft.mode = w.mode; draw(card, draft, version, people, reload) })
    tiles.append(b)
  }
  card.append(tiles)
  const box = el('div', { class: 'owner-pay-detail' })
  const chip = (label: string, on: boolean, pick: () => void): HTMLElement => {
    const b = el('button', { type: 'button', class: `owner-pay-chip${on ? ' is-on' : ''}`, 'aria-pressed': String(on) }, label)
    b.addEventListener('click', () => { pick(); draw(card, draft, version, people, reload) })
    return b
  }
  if (draft.mode === 'keep') box.append(el('p', {}, 'Close out the day shows "You keep" at the bottom. Nothing to set.'))
  if (draft.mode === 'ask') box.append(el('p', {}, 'Close out the day ends with "What you took" on each day you are out working. You type it.'))
  if (draft.mode === 'wage') {
    const wage = el('input', { class: 'owner-pay-money', inputmode: 'decimal', value: draft.wage, placeholder: '0.00', 'aria-label': 'Your wage' }) as HTMLInputElement
    wage.addEventListener('input', () => { draft.wage = wage.value })
    box.append(el('div', { class: 'owner-pay-chips' }, chip('A day rate', draft.wage_kind === 'day', () => { draft.wage_kind = 'day' }), chip('A set amount a month', draft.wage_kind === 'month', () => { draft.wage_kind = 'month' })),
      el('label', { class: 'owner-pay-line' }, '£', wage, draft.wage_kind === 'day' ? 'a day you work' : 'a month'),
      el('p', { class: 'muted' }, draft.wage_kind === 'day' ? 'It fills in on each close-out of a day you work.' : 'Taken off once a month in Money, not on each day.'))
  }
  if (draft.mode === 'split') {
    box.append(el('div', { class: 'owner-pay-chips' }, el('span', { class: 'owner-pay-chips-label' }, 'The % is of:'),
      chip('what’s left after workers', draft.base === 'after', () => { draft.base = 'after' }), chip('the whole day’s work', draft.base === 'all', () => { draft.base = 'all' })))
    const names = el('datalist', { id: 'owner-pay-names' })
    for (const p of people) if (p.worker_name) names.append(el('option', { value: p.worker_name }))
    box.append(names)
    draft.partners.forEach((p, i) => {
      const name = el('input', { value: p.name, list: 'owner-pay-names', placeholder: 'Name on their visits', 'aria-label': `Partner ${i + 1}'s name` }) as HTMLInputElement
      const pct = el('input', { value: p.pct, inputmode: 'numeric', class: 'owner-pay-pct', 'aria-label': `Partner ${i + 1}'s percent` }) as HTMLInputElement
      const min = el('input', { value: p.min, inputmode: 'decimal', class: 'owner-pay-money', placeholder: 'none', 'aria-label': `Partner ${i + 1}'s minimum a day` }) as HTMLInputElement
      name.addEventListener('input', () => { p.name = name.value })
      pct.addEventListener('input', () => { p.pct = pct.value })
      min.addEventListener('input', () => { p.min = min.value })
      const out = el('button', { type: 'button', class: 'link-button', 'aria-label': `Take partner ${i + 1} off` }, 'Remove')
      out.addEventListener('click', () => { draft.partners.splice(i, 1); draw(card, draft, version, people, reload) })
      box.append(el('div', { class: 'owner-pay-partner' }, name, el('label', { class: 'owner-pay-line' }, pct, '%'), el('label', { class: 'owner-pay-line' }, 'at least £', min), ...(draft.partners.length > 1 ? [out] : [])))
    })
    const add = el('button', { type: 'button', class: 'btn-secondary' }, '+ Add a partner')
    add.addEventListener('click', () => { draft.partners.push({ name: '', pct: '', min: '' }); draw(card, draft, version, people, reload) })
    box.append(add, el('p', { class: 'muted' }, `${keeps} keeps the rest. A partner's share and minimum count only on days they are out working.`))
  }
  const err = el('p', { class: 'form-error', role: 'alert' })
  const save = el('button', { type: 'button', class: 'btn-primary' }, 'Save how you get paid')
  save.addEventListener('click', () => void send(draft, version, save, err, reload))
  card.append(box, err, save)

  // How your people are paid: People's own pay line, for each worker, and its Add someone.
  if (worksAlone()) return
  const team = el('div', { class: 'owner-pay-people' }, el('h3', {}, 'How your people are paid'), el('p', { class: 'muted' }, 'The same as on People: change it in either place.'))
  for (const p of people.filter((x) => x.role === 'worker')) {
    const perr = el('p', { class: 'form-error', role: 'alert' })
    team.append(el('div', { class: 'owner-pay-person' }, el('strong', {}, p.worker_name ?? p.email), payLine(p, reload, perr), perr))
  }
  const addSomeone = el('button', { type: 'button', class: 'btn-secondary' }, '+ Add someone')
  addSomeone.addEventListener('click', () => openAdd(null, addSomeone, reload))
  team.append(addSomeone)
  card.append(team)
}

async function send(draft: Draft, version: number, button: HTMLElement, err: HTMLElement, reload: () => void): Promise<void> {
  err.textContent = ''
  const body: Record<string, unknown> = { version, mode: draft.mode, base: draft.base }
  try {
    if (draft.mode === 'wage') {
      const w = parseMoney(draft.wage)
      if (w === null) { err.textContent = 'Type your wage.'; return }
      body.wage_kind = draft.wage_kind
      body.wage_pence = w
    }
    if (draft.mode === 'split') {
      body.partners = draft.partners.map((p, i) => {
        const pct = Number(p.pct)
        if (!p.name.trim()) throw new Error(`Type partner ${i + 1}'s name, as on their visits.`)
        if (!Number.isInteger(pct) || pct < 1 || pct > 100) throw new Error(`${p.name.trim()}'s percent is a whole number, 1 to 100.`)
        return { name: p.name.trim(), pct, min_pence: parseMoney(p.min) }
      })
    }
  } catch (e) { err.textContent = (e as Error).message; return }
  ;(button as HTMLButtonElement).disabled = true
  buzz('light')
  const ack = await mutate({ label: 'How you get paid', method: 'PATCH', path: '/api/owner-pay', body })
  ;(button as HTMLButtonElement).disabled = false
  if (ack.ok) { toast('Saved: Close out the day uses it from now'); reload(); return }
  if (ack.status === 0) { toast('Saved on this phone. It sends when there is signal.'); return }
  err.textContent = ack.status === 409 ? 'Changed somewhere else just now. Showing it as it is.' : (ack.error ?? 'Did not save. Try again.')
  if (ack.status === 409) reload()
}

